const $ = require('jquery');
const Cookies = require('./cookie');

/**
 * Generates a unique ID based on the current timestamp and a random string
 *
 * @returns {string} A unique ID consisting of a timestamp followed by a hyphen and a 13-character random alphanumeric string
 */
function generateID() {
    const timestamp = new Date().getTime();
    const randomData = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${randomData}`;
}

const optimizelyExperimentation = {
    /**
     * Initializes the Optimizely SDK and sets the user context
     */
    _initialize() {
        const experimentationInstanceDataJSON = $('meta[name="experimentationInstanceData"]').attr('content');
        if (!experimentationInstanceDataJSON) {
            // Optimizely experimentation is disabled
            this._loadingPromise = Promise.resolve();
            return;
        }
        const experimentationInstanceData = JSON.parse(experimentationInstanceDataJSON);
        const {API_KEY, activeFlagsJSON, customerId} = experimentationInstanceData;
        this.isTrackingAllowed = experimentationInstanceData.isTrackingAllowed;

        let userId;
        if (customerId) {
            // Logged in customer
            userId = customerId;
        } else if (sessionStorage.getItem('optiUserId')) {
            // Guest customer with existing optiUserId
            userId = sessionStorage.getItem('optiUserId');
        } else {
            // Guest customer on first entry
            userId = generateID();
            sessionStorage.setItem('optiUserId', userId);
        }

        this.ACTIVE_FLAGS_MAPPING = JSON.parse(activeFlagsJSON);

        this._optimizely = window.optimizelySdk.createInstance({
            sdkKey: API_KEY
        });

        // Store the initialization promise
        this._loadingPromise = this._optimizely.onReady().then(({success, reason}) => {
            if (success) {
                // Set audience test cookie
                const testUserCookie = Cookies.get('optimizely_test_user');
                if (testUserCookie && testUserCookie.length) {
                    userId = testUserCookie;
                }
                this._user = this._optimizely.createUserContext(userId);
            } else {
                console.error(reason);
            }
        });

        this._eventsBuffer = [];
    },

    /**
     * Gets the Optimizely user context if possible
     *
     * @returns {Object|null} Optimizely user context object or null if Optimizely experimentation is disabled or failed to load
     */
    async _getUserContext() {
        // Check if no user context is created
        if (!this._user) {
            // User context is not created. Start the initialization if it's not started yet
            if (!this._loadingPromise) {
                this._initialize();
            }
            // Wait for the initialization to complete
            await this._loadingPromise;
        }
        return this._user;
    },

    /**
     * Tracks an analytics event.
     *
     * @param {string} eventLabel - The label of the event to be tracked.
     * @param {Object} tags - An object containing data that should be tracked.
     */
    _analyticsTrackEventDecision(eventLabel, tags) {
        if (!eventLabel || !tags || !this._eventsBuffer) return;
        const optimizelyDecisionDetails = {};

        optimizelyDecisionDetails.featureFlagId = tags.flagKey;
        optimizelyDecisionDetails.ruleId = tags.ruleKey;
        optimizelyDecisionDetails.variationId = tags.variationKey;
        optimizelyDecisionDetails.variables = tags.variables;

        const decisionDetailsString = JSON.stringify(optimizelyDecisionDetails);
        const isUnique = !this._eventsBuffer.some(event => JSON.stringify(event) === decisionDetailsString);

        if (isUnique) {
            // Push to events buffer
            this._eventsBuffer.push(optimizelyDecisionDetails);

            // Push to data layer
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: eventLabel,
                optimizelyDecisionDetails
            });
        }
    },

    /**
     * Function that decides which feature flag variation to use
     *
     * @param {string} context - context for which to load the flags. Current options are 'plp' and 'pdp
     * @returns {Promise} promise who is going to give the decisionResultVariations object witht the final decision
     */
    async decideFeatureFlagVariation(context) {
        const user = await this._getUserContext();
        const OPTIMIZELY_FEATURE_TESTING_EVENT = 'optimizely_decision_info';

        if (!user) {
            return {error: true};
        }

        const normalizedContext = context.toLowerCase();

        const decisionMapping = {
            pdp: {
                variationKeySizing: this.ACTIVE_FLAGS_MAPPING.US_EU_SIZING,
                variationKeyImageStacking: this.ACTIVE_FLAGS_MAPPING.DESKTOP_PRODUCT_IMAGES_STACKED,
                variationKeyMobileImageTextOrder: this.ACTIVE_FLAGS_MAPPING.MOBILE_PDP_IMAGERY_TEXT,
                variationKeyMobileStickyAddToCart: this.ACTIVE_FLAGS_MAPPING.MOBILE_PDP_STICKY_ADD_TO_CART
            },
            plp: {
                variationKeyQuickshop: this.ACTIVE_FLAGS_MAPPING.DESKTOP_PLP_QUICK_SHOP
            },
            search: {
                variationKeyStickySearch: this.ACTIVE_FLAGS_MAPPING.MOBILE_STICKY_SEARCH_BAR
            },
            cart: {
                variationKeyStickyProceedCheckout: this.ACTIVE_FLAGS_MAPPING.MOBILE_CART_PROCEED_TO_CHECKOUT
            },
            homepage: {
                variationHomepageFlyin: this.ACTIVE_FLAGS_MAPPING.HOMEPAGE_RECOMMENDATIONS_FLYIN
            }
        };

        const flagsMapping = decisionMapping[normalizedContext];
        if (!flagsMapping) {
            return {error: true, message: 'Invalid context'};
        }
        const decisionResultVariations = {};

        for (const resultKey in flagsMapping) {
            if (Object.prototype.hasOwnProperty.call(flagsMapping, resultKey)) {
                const flag = flagsMapping[resultKey];
                const decision = user.decide(flag);
                decisionResultVariations[resultKey] = decision.variationKey;

                this._analyticsTrackEventDecision(OPTIMIZELY_FEATURE_TESTING_EVENT, decision);
            }
        }

        return decisionResultVariations;
    },

    /**
     * Adds event listeners to send track events
     *
     * @param {string} eventLabel - label of event to be trakced
     * @param {Object} tags - tag object containing data that should be tracked
     */
    async trackEvent(eventLabel, tags) {
        if (this.isTrackingAllowed) {
            const user = await this._getUserContext();
            if (user) {
                user.trackEvent(eventLabel, tags);
            } else {
                console.error('Failed to initialize optimizely user context.');
            }
        }
    }
};

module.exports = optimizelyExperimentation;
